import { useCallback } from "react"
import { useAuthApi } from "../../infra/auth"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { setToken, setSessionConfig, resetAuthState } from "../../application/states/auth/auth-slice"
import { setCurrentUserState, setCurrentUserModulesState } from "../../application/states/user"
import { useLocalStorageService } from "../local-storage"
import { executeAlert } from "../../application/states/toast-alert/toast-alert"
import { useNavigationService } from "../navigation"
import * as jose from 'jose'
import { LegacyProductsList, ProdutosEnum } from '../../presentation/assets/helpers/ProdutosEnum'

export const useAuthService = () => {
  const dispatch = useAppDispatch()
  const token = useAppSelector((state) => state.auth.token)
  const isValid = useAppSelector((state) => state.auth.isValid)
  const user = useAppSelector((state) => state.auth.user)

  const { loginApi, insertAcessoApi, generateLegacyRedirectApi, refreshApi, getSessionTimeoutApi } = useAuthApi()
  const { setItem, getItem, removeItem } = useLocalStorageService()
  const { navigateTo } = useNavigationService()

  const login = useCallback(async (data: {
    loginCliente: string
    loginUsuario: string
    senhaUsuario: string
    idProduto?: number
  }) => {
    try {
      const response = await loginApi(data)

      const activeToken = response.token || response.Token
      const autenticado = response.autenticado ?? response.Autenticado
      const mensagem = response.mensagem ?? response.mensagem

      if (autenticado && activeToken) {
        dispatch(setToken({ token: activeToken }))

        const timestamp = Date.now()
        setItem({
          key: 'ACCESS_TOKEN',
          value: activeToken
        })
        setItem({
          key: 'LOGIN_TIMESTAMP',
          value: String(timestamp)
        })
        dispatch(setSessionConfig({
          timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
          timestamp: timestamp
        }))

        // Mapear informações do usuário do V3 para salvar no estado e localStorage
        const userPayload = {
          IdUsuario: response.IdUsuario,
          Nome: response.NomeUsuario,
          LoginUsuario: response.LoginUsuario,
          Email: response.EmailUsuario,
          IdPerfil: response.IdPerfil,
          Perfil: response.Perfil
        }

        dispatch(setCurrentUserState(userPayload))

        setItem({
          key: 'USER_INFO',
          value: JSON.stringify(userPayload)
        })

        // NOVO: Salvar módulos recebidos no login diretamente no USER_MODULES
        const resAny = response as any;
        const produtos = resAny.Produtos ?? resAny.produtos ?? resAny.result?.Produtos ?? resAny.result?.produtos
        if (produtos && Array.isArray(produtos)) {
          const moduleInfo = produtos.map((p: any) => ({
            IdProduto: p.IdProduto ?? p.idProduto,
            Nome: p.Descricao ?? p.descricao
          }))
          setItem({
            key: 'USER_MODULES',
            value: JSON.stringify(moduleInfo)
          })
          dispatch(setCurrentUserModulesState(moduleInfo))
        }

        dispatch(executeAlert({
          message: 'Login realizado com sucesso!',
          type: 'success'
        }))

        navigateTo({
          route: '/modulos'
        })
      } else {
        throw new Error(mensagem || "Falha ao realizar login.")
      }
    } catch (error: any) {
      console.error("Erro no login:", error)
      const errorMsg = error.response?.data?.result?.message || error.response?.data?.result?.error || error.message || "Erro ao fazer login."

      dispatch(executeAlert({
        message: errorMsg,
        type: 'error'
      }))
      throw error
    }
  }, [dispatch, loginApi, setItem, navigateTo])

  const logout = useCallback((customMessage?: string, customType?: 'info' | 'error' | 'success' | 'warning', skipRedirect?: boolean) => {
    dispatch(resetAuthState())
    dispatch(setCurrentUserState(undefined))
    removeItem({
      key: 'ACCESS_TOKEN'
    })
    removeItem({
      key: 'USER_INFO'
    })
    removeItem({
      key: 'USER_MODULES'
    })
    removeItem({
      key: 'LOGIN_TIMESTAMP'
    })
    dispatch(executeAlert({
      message: customMessage || 'Sessão encerrada.',
      type: customType || 'info'
    }))
    if (!skipRedirect) {
      navigateTo({
        route: '/authentication/login'
      })
    }
  }, [dispatch, removeItem, navigateTo])

  const fetchSessionConfig = useCallback(async () => {
    try {
      const response = await getSessionTimeoutApi()
      const { sessionTimeoutSeconds } = response

      dispatch(setSessionConfig({
        timeout: sessionTimeoutSeconds
      }))

      return sessionTimeoutSeconds
    } catch (error) {
      return  Number(process.env.SESSION_TIMEOUT_SECONDS)
    }
  }, [getSessionTimeoutApi, dispatch])

  const setupSession = useCallback(async () => {
    const tokenStorage = getItem({
      key: 'ACCESS_TOKEN'
    })
    const userInfoStorage = getItem({
      key: 'USER_INFO'
    })
    const userModulesStorage = getItem({
      key: 'USER_MODULES'
    })

    if (tokenStorage) {
      try {
        const decoded = jose.decodeJwt(tokenStorage)
        const currentTime = Math.floor(Date.now() / 1000);

        if (decoded.exp && decoded.exp <= currentTime) {
          console.warn('Sessão expirou no setupSession');
          logout()
          return;
        }

        dispatch(setToken({ token: tokenStorage }))

        if (userInfoStorage) {
          dispatch(setCurrentUserState(JSON.parse(userInfoStorage)))
        }

        if (userModulesStorage) {
          dispatch(setCurrentUserModulesState(JSON.parse(userModulesStorage)))
        }

        const timestampStorage = getItem({
          key: 'LOGIN_TIMESTAMP'
        })
        if (timestampStorage) {
          dispatch(setSessionConfig({
            timeout:  Number(process.env.SESSION_TIMEOUT_SECONDS),
            timestamp: Number(timestampStorage)
          }))
        } else {
          const now = Date.now()
          setItem({ key: 'LOGIN_TIMESTAMP', value: String(now) })
          dispatch(setSessionConfig({
            timeout:  Number(process.env.SESSION_TIMEOUT_SECONDS),
            timestamp: now
          }))
        }
        await fetchSessionConfig()
      } catch (e) {
        console.error("Erro ao decodificar token no setupSession:", e)
        logout()
      }
    } else {
      dispatch(resetAuthState())
      dispatch(setCurrentUserState(undefined))
    }
  }, [dispatch, getItem, logout])

  const refresh = useCallback(async () => {
    try {
      if (!token) return

      const response = await refreshApi(token)
      const autenticado = response.Autenticado ?? (response as any).autenticado
      const novoToken = response.Token ?? (response as any).token
      const mensagem = response.Mensagem ?? (response as any).mensagem

      if (autenticado && novoToken) {
        dispatch(setToken({ token: novoToken }))
        setItem({
          key: 'ACCESS_TOKEN',
          value: novoToken
        })

        // Atualizar metadados do usuário caso disponíveis na renovação
        const idUsuario = response.IdUsuario ?? (response as any).idUsuario
        const nomeUsuario = response.NomeUsuario ?? (response as any).nomeUsuario
        const loginUsuario = response.LoginUsuario ?? (response as any).loginUsuario
        const emailUsuario = response.EmailUsuario ?? (response as any).emailUsuario
        const idPerfil = response.IdPerfil ?? (response as any).idPerfil
        const perfil = response.Perfil ?? (response as any).perfil

        if (idUsuario) {
          const userPayload = {
            IdUsuario: idUsuario,
            Nome: nomeUsuario,
            LoginUsuario: loginUsuario,
            Email: emailUsuario,
            IdPerfil: idPerfil,
            Perfil: perfil
          }
          dispatch(setCurrentUserState(userPayload))
          setItem({
            key: 'USER_INFO',
            value: JSON.stringify(userPayload)
          })
        }

        // Atualizar módulos ativos em tempo real com base no refresh
        const produtos = (response as any).Produtos ?? (response as any).produtos
        if (produtos && Array.isArray(produtos)) {
          const moduleInfo = produtos.map((p: any) => ({
            IdProduto: p.IdProduto ?? p.idProduto,
            Nome: p.Descricao ?? p.descricao
          }))
          dispatch(setCurrentUserModulesState(moduleInfo))
        }
      } else {
        console.warn("Refresh retornou não autenticado:", mensagem)
        logout(mensagem || 'IP não permitido ou sessão bloqueada.', 'error')
      }
    } catch (error: any) {
      console.error("Erro ao renovar sessão no background:", error)
      const errorMsg = error.response?.data?.Mensagem ?? error.response?.data?.mensagem ?? error.message ?? 'Falha de comunicação no refresh.'
      logout(errorMsg, 'error')
    }
  }, [token, refreshApi, setItem, dispatch, logout])

  const insertAcesso = useCallback(async (idProduto: number): Promise<string | null> => {
    try {
      const result = await insertAcessoApi({
        IdProduto: idProduto
      })

      dispatch(setToken({ token: result.accessToken }))

      setItem({
        key: 'ACCESS_TOKEN',
        value: result.accessToken
      })

      if (result.usuario) {
        const existingUserInfoStr = getItem({ key: 'USER_INFO' })
        let existingUser: any = {}
        if (existingUserInfoStr) {
          try {
            existingUser = JSON.parse(existingUserInfoStr)
          } catch { }
        }

        const userPayload = {
          IdUsuario: result.usuario.IdUsuario || existingUser.IdUsuario,
          Nome: result.usuario.Nome || existingUser.Nome,
          LoginUsuario: result.usuario.LoginUsuario || existingUser.LoginUsuario,
          Email: result.usuario.Email || existingUser.Email,
          IdPerfil: result.usuario.IdPerfil || existingUser.IdPerfil,
          Perfil: result.usuario.Perfil || existingUser.Perfil
        }
        dispatch(setCurrentUserState(userPayload))
        setItem({
          key: 'USER_INFO',
          value: JSON.stringify(userPayload)
        })
      }

      if (Number(idProduto) !== ProdutosEnum.AUTH_VELES && LegacyProductsList.includes(Number(idProduto))) {
        const redirectResult = await generateLegacyRedirectApi({
          IdAcesso: result.acesso.IdAcesso,
          IdProduto: idProduto
        })
        return redirectResult.redirectUrl
      }

      return null
    } catch (error: any) {
      console.error("Erro no insertAcesso:", error)
      const errorMsg = error.response?.data?.result?.message || error.response?.data?.result?.error || error.message || "Erro ao acessar o módulo."
      dispatch(executeAlert({
        message: errorMsg,
        type: 'error'
      }))
      throw error
    }
  }, [dispatch, insertAcessoApi, generateLegacyRedirectApi, setItem])

  const resetSessionTimer = useCallback(() => {
    const now = Date.now()
    setItem({
      key: 'LOGIN_TIMESTAMP',
      value: String(now)
    })
    dispatch(setSessionConfig({
      timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
      timestamp: now
    }))
  }, [dispatch, setItem])

  return {
    token,
    isValid,
    user,
    login,
    logout,
    setupSession,
    refresh,
    insertAcesso,
    fetchSessionConfig,
    resetSessionTimer
  }
}

